import type {
  FilterOption,
  ViewLayout,
} from 'features/knowledgeDocumentViewer/types'
import {
  PERMISSION_CANTILEVER_POWER_DOCUMENT_NAVIGATOR_ADMIN,
  PERMISSION_CANTILEVER_POWER_DOCUMENT_NAVIGATOR_EDITOR,
  PERMISSION_CANTILEVER_POWER_DOCUMENT_NAVIGATOR_VIEWER,
  ROLE_CANTILEVER_DOCUMENT_NAVIGATOR_EDITOR,
  ROLE_CANTILEVER_DOCUMENT_NAVIGATOR_UPLOADER,
  ROLE_CANTILEVER_DOCUMENT_NAVIGATOR_VIEWER,
  type RolodexPermissionsValues,
} from 'types'
import { z } from 'zod'

export const VIEWER_BUG_REPORT_SLACK_CHANNEL_ID = 'C0BD4JXTY3S'
export const VIEWER_BUG_REPORT_SUBJECT = 'Forecast document viewer bug'
export const VIEWER_BUG_REPORT_ALLOWED_ROLES: RolodexPermissionsValues[] = [
  PERMISSION_CANTILEVER_POWER_DOCUMENT_NAVIGATOR_VIEWER,
  PERMISSION_CANTILEVER_POWER_DOCUMENT_NAVIGATOR_EDITOR,
  PERMISSION_CANTILEVER_POWER_DOCUMENT_NAVIGATOR_ADMIN,
  ROLE_CANTILEVER_DOCUMENT_NAVIGATOR_VIEWER,
  ROLE_CANTILEVER_DOCUMENT_NAVIGATOR_UPLOADER,
  ROLE_CANTILEVER_DOCUMENT_NAVIGATOR_EDITOR,
]
const SLACK_SECTION_TEXT_LIMIT = 3000
const TRUNCATION_NOTICE = '\n\n[truncated]'

export const bugReportPrioritySchema = z.enum(['high', 'medium', 'low'])
export const bugReportTypeSchema = z.enum(['bug', 'feature', 'improvement'])
export const bugReportAgentModeSchema = z.enum([
  'ticket_only',
  'review',
  'resolve',
])

export type BugReportPriority = z.infer<typeof bugReportPrioritySchema>
export type BugReportType = z.infer<typeof bugReportTypeSchema>
export type BugReportAgentMode = z.infer<typeof bugReportAgentModeSchema>

export const bugReportClassificationSchema = z.strictObject({
  priority: bugReportPrioritySchema,
  type: bugReportTypeSchema,
  agentMode: bugReportAgentModeSchema,
})

export type BugReportClassification = z.infer<
  typeof bugReportClassificationSchema
>

export const DEFAULT_BUG_REPORT_PRIORITY: BugReportPriority = 'medium'
export const DEFAULT_BUG_REPORT_TYPE: BugReportType = 'bug'
export const DEFAULT_BUG_REPORT_AGENT_MODE: BugReportAgentMode = 'resolve'

export const DEFAULT_BUG_REPORT_CLASSIFICATION: BugReportClassification = {
  priority: DEFAULT_BUG_REPORT_PRIORITY,
  type: DEFAULT_BUG_REPORT_TYPE,
  agentMode: DEFAULT_BUG_REPORT_AGENT_MODE,
}

/* eslint-disable lingui/no-unlocalized-strings */
export const BUG_REPORT_PRIORITY_SLACK_LABELS: Record<
  BugReportPriority,
  string
> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

export const BUG_REPORT_TYPE_SLACK_LABELS: Record<BugReportType, string> = {
  bug: 'Bug',
  feature: 'Feature',
  improvement: 'Improvement',
}

export const BUG_REPORT_AGENT_MODE_SLACK_LABELS: Record<
  BugReportAgentMode,
  string
> = {
  ticket_only: 'Ticket only',
  review: 'Review and propose',
  resolve: 'Fully resolve',
}

export function buildReporterClassificationBlock(
  classification: BugReportClassification
): string {
  return [
    'Reporter classification',
    `- Priority: ${BUG_REPORT_PRIORITY_SLACK_LABELS[classification.priority]}`,
    `- Type: ${BUG_REPORT_TYPE_SLACK_LABELS[classification.type]}`,
    `- Agent mode: ${BUG_REPORT_AGENT_MODE_SLACK_LABELS[classification.agentMode]}`,
  ].join('\n')
}

export type ViewerBugReportContext = {
  documentId?: string
  documentTitle?: string
  documentFilename?: string
  reportedByEmail?: string
  siteId: string
  pageNumber?: number
  totalPages?: number
  searchQuery?: string
  selectedBoxIds?: string[]
  filterOptions?: FilterOption
  viewLayout?: ViewLayout
  forecastVersion?: string
  recentActions: string[]
}

function formatOptionalLine(
  label: string,
  value: string | undefined
): string[] {
  return value ? [`- ${label}: ${value}`] : []
}

function getBrowserContext(): string[] {
  if (typeof window === 'undefined') return []

  return [
    `- URL: ${window.location.href}`,
    `- Browser: ${window.navigator.userAgent}`,
    `- Viewport: ${window.innerWidth}x${window.innerHeight}`,
    `- Timestamp: ${new Date().toISOString()}`,
  ]
}

function getSelectedTextLine(): string[] {
  if (typeof window === 'undefined') return []

  const selectedText = window.getSelection()?.toString().trim()
  if (!selectedText) return []

  const snippet =
    selectedText.length > 250
      ? `${selectedText.slice(0, 250)}...`
      : selectedText
  return [`- Selected text: ${snippet}`]
}

export function buildViewerBugReportUserBody(): string {
  return ''
}

function buildViewerBugReportReceiverContext(
  context: ViewerBugReportContext
): string {
  const title = context.documentTitle ?? context.documentFilename
  const selectedBoxes = context.selectedBoxIds?.join(', ')
  const page =
    context.pageNumber !== undefined && context.totalPages !== undefined
      ? `${context.pageNumber} / ${context.totalPages}`
      : undefined
  const recentActions =
    context.recentActions.length > 0
      ? context.recentActions.map((action, index) => `${index + 1}. ${action}`)
      : ['No recent document actions captured.']

  return [
    'Auto-captured context',
    ...getBrowserContext(),
    ...formatOptionalLine('Reported by', context.reportedByEmail),
    ...formatOptionalLine('Document ID', context.documentId),
    ...formatOptionalLine('Document title', title),
    ...formatOptionalLine('Filename', context.documentFilename),
    `- Site ID: ${context.siteId}`,
    ...formatOptionalLine('Page', page),
    ...formatOptionalLine('Search query', context.searchQuery),
    ...formatOptionalLine('Selected box IDs', selectedBoxes || undefined),
    ...getSelectedTextLine(),
    ...formatOptionalLine('Filter mode', context.filterOptions),
    ...formatOptionalLine('Layout', context.viewLayout),
    ...formatOptionalLine('Forecast version', context.forecastVersion),
    '',
    'Recent document actions',
    ...recentActions,
  ].join('\n')
}

export function buildViewerBugReportSubject(
  context: ViewerBugReportContext
): string {
  const subjectParts = [
    VIEWER_BUG_REPORT_SUBJECT,
    context.reportedByEmail,
    context.siteId,
  ].filter(Boolean)

  return subjectParts.join(' - ')
}

function truncateWithNotice(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength - TRUNCATION_NOTICE.length)}${TRUNCATION_NOTICE}`
}

function buildSlackBlockText(
  subject: string,
  userReportBody: string,
  classificationBlock: string,
  receiverContext: string
): string {
  const prefix = `*${subject}*\n`
  const staticAfterBody = `${classificationBlock}\n\n${receiverContext}`
  const fullText = `${prefix}${userReportBody.trim()}\n\n${staticAfterBody}`
  if (fullText.length <= SLACK_SECTION_TEXT_LIMIT) return fullText

  const staticTextLength =
    prefix.length + staticAfterBody.length + TRUNCATION_NOTICE.length + 2
  const maxUserBodyLength = SLACK_SECTION_TEXT_LIMIT - staticTextLength
  if (maxUserBodyLength > 0) {
    return `${prefix}${truncateWithNotice(
      userReportBody.trim(),
      maxUserBodyLength
    )}\n\n${staticAfterBody}`
  }

  return truncateWithNotice(fullText, SLACK_SECTION_TEXT_LIMIT)
}

export function buildViewerBugReportSlackPayload(
  context: ViewerBugReportContext,
  userReportBody: string,
  classification: BugReportClassification = DEFAULT_BUG_REPORT_CLASSIFICATION
) {
  const subject = buildViewerBugReportSubject(context)
  const classificationBlock = buildReporterClassificationBlock(classification)
  const receiverContext = buildViewerBugReportReceiverContext(context)

  return {
    channel: VIEWER_BUG_REPORT_SLACK_CHANNEL_ID,
    text: subject,
    blocks: [
      {
        type: 'section',
        text: {
          type: 'mrkdwn',
          text: buildSlackBlockText(
            subject,
            userReportBody,
            classificationBlock,
            receiverContext
          ),
        },
      },
    ],
  }
}
